/**
 * User Settings, without React.
 *
 * The same draft-then-Save shape as Feature Enablements, over values that are
 * not booleans: a draft here holds whatever the setting holds, and `null`
 * means clear it so the default applies again.
 */

import type {
  SaveUserSettingsRequest,
  UserSettingListItem,
  UserSettingsSubject,
} from "@wallyo/contracts/console/configuration";
import type { SettingValue } from "@wallyo/contracts/settings";

import { t } from "../../i18n";
import { REASON_MIN_LENGTH } from "../team/team-writes";

/** What each moved row says now. `null` clears it. */
export type SettingsDraft = Readonly<Record<string, SettingValue | null>>;

export const EMPTY_SETTINGS_DRAFT: SettingsDraft = {};

export function isSettingChanged({ settingId }: UserSettingListItem, draft: SettingsDraft): boolean {
  return draft[settingId] !== undefined;
}

/** What the editor shows: the draft when moved, the resolved value otherwise. */
export function shownSettingValue(setting: UserSettingListItem, draft: SettingsDraft): SettingValue {
  const drafted = draft[setting.settingId];
  if (drafted === undefined) return setting.value;
  // Cleared: it will read the default, which is exactly what to show.
  return drafted ?? setting.defaultValue;
}

/** A value that equals what loaded, and was already theirs, is not a change. */
export function withSettingValue(
  draft: SettingsDraft,
  setting: UserSettingListItem,
  value: SettingValue | null,
): SettingsDraft {
  const { [setting.settingId]: _dropped, ...rest } = draft;
  if (value !== null && setting.setHere && same(value, setting.value)) return rest;
  return { ...rest, [setting.settingId]: value };
}

/** Lists compare by contents, not by identity. */
function same(one: SettingValue, other: SettingValue): boolean {
  if (Array.isArray(one) && Array.isArray(other)) {
    return one.length === other.length && one.every((each, index) => each === other[index]);
  }
  return one === other;
}

export function changedSettings(
  settings: readonly UserSettingListItem[],
  draft: SettingsDraft,
): readonly UserSettingListItem[] {
  return settings.filter((setting) => isSettingChanged(setting, draft));
}

export function saveSettingsRequest(
  subject: UserSettingsSubject,
  reason: string,
  settings: readonly UserSettingListItem[],
  draft: SettingsDraft,
): SaveUserSettingsRequest {
  return {
    subject,
    reason: reason.trim(),
    changes: changedSettings(settings, draft).map(({ settingId, valueVersion }) => ({
      settingId,
      value: draft[settingId] ?? null,
      ...(valueVersion === undefined ? {} : { version: valueVersion }),
    })),
  };
}

export function settingsSaveProblem(
  reason: string,
  settings: readonly UserSettingListItem[],
  draft: SettingsDraft,
): string | null {
  const changed = changedSettings(settings, draft);
  if (changed.length === 0) return t("features.save.nothing");
  if (changed.some((setting) => !setting.editable)) return t("features.save.locked");
  if (reason.trim().length < REASON_MIN_LENGTH) return t("features.save.reasonNeeded");
  return null;
}
